import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { adminNavigationConfig } from '@/common/constants/navigationConfigs';
import { AuthContext } from '@/common/contexts/authContext';
import {
	filterSearchItems,
	getSearchItems,
	getSectionChips,
	getSectionColor,
	getTokens,
	groupSearchItems,
} from '../helpers/getSearchItems';
import { KEYS, SHORTCUT_KEY } from '../constants/pageSearch.constants';

export const usePageSearch = ({ show, setShow }) => {
	const { checkAccess } = useContext(AuthContext);
	const navigate = useNavigate();
	const [query, setQuery] = useState('');
	const [section, setSection] = useState('');
	const [activeIndex, setActiveIndex] = useState(0);

	const items = useMemo(
		() => getSearchItems(adminNavigationConfig, checkAccess),
		[checkAccess],
	);
	const sections = useMemo(
		() => getSectionChips(items).map((chip) => chip.section),
		[items],
	);
	const tokens = useMemo(() => getTokens(query), [query]);
	const chips = useMemo(
		() => getSectionChips(filterSearchItems(items, query, '')),
		[items, query],
	);
	const totalCount = useMemo(
		() => chips.reduce((sum, chip) => sum + chip.count, 0),
		[chips],
	);

	const { groups, orderedItems } = useMemo(() => {
		const grouped = groupSearchItems(filterSearchItems(items, query, section));
		let index = 0;

		return {
			groups: grouped.map((group) => ({
				section: group.section,
				color: getSectionColor(sections, group.section),
				rows: group.items.map((item) => ({ item, index: index++ })),
			})),
			orderedItems: grouped.flatMap((group) => group.items),
		};
	}, [items, query, section, sections]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			const isShortcut =
				(event.metaKey || event.ctrlKey) &&
				event.key.toLowerCase() === SHORTCUT_KEY;

			if (isShortcut) {
				event.preventDefault();
				setShow((prev) => !prev);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [setShow]);

	useEffect(() => {
		if (!show) {
			setQuery('');
			setSection('');
			setActiveIndex(0);
		}
	}, [show]);

	const handleQueryChange = (value) => {
		setQuery(value);
		setActiveIndex(0);
	};

	const handleSectionChange = (value) => {
		setSection(value);
		setActiveIndex(0);
	};

	const handleSelect = (item) => {
		setShow(false);
		navigate(item.link);
	};

	const handleInputKeyDown = (event) => {
		if (event.key === KEYS.ESCAPE) {
			setShow(false);
		}
		if (event.key === KEYS.ARROW_DOWN) {
			event.preventDefault();
			setActiveIndex((prev) => Math.min(prev + 1, orderedItems.length - 1));
		}
		if (event.key === KEYS.ARROW_UP) {
			event.preventDefault();
			setActiveIndex((prev) => Math.max(prev - 1, 0));
		}
		if (event.key === KEYS.ENTER && orderedItems[activeIndex]) {
			handleSelect(orderedItems[activeIndex]);
		}
	};

	return {
		query,
		section,
		activeIndex,
		tokens,
		chips,
		totalCount,
		groups,
		getChipColor: (chipSection) => getSectionColor(sections, chipSection),
		setActiveIndex,
		handleQueryChange,
		handleSectionChange,
		handleSelect,
		handleInputKeyDown,
	};
};
